import { create } from "zustand"
import { combine } from "zustand/middleware"
import { ownNotActiveStatuses } from "@utils/const"
import qs from "qs"
import IsEqual from "lodash/isEqual"
import { getSearchByCreatedAt } from "@lib/api/user"
import { omit } from "lodash"

const filters = {
    price_lte: 55000,
    price_per_point_lte: 305,
    points_on_contract_lte: 1600,
    points_on_contract_gte: 20,
    resort: {
        short_name_in: [] as string[]
    },
    use_month_in: [] as string[],
    origin_in: [] as string[],
    listing_status_in: [] as string[],
    listing_status_nin: ownNotActiveStatuses,
    stars_gte: 1,
    sort: "parsed_date:desc"
}

const filterKeys = Object.keys(filters)

const shortFilterName = {
    pr: "price_lte",
    pp: "price_per_point_lte",
    pc: "points_on_contract_lte",
    pg: "points_on_contract_gte",
    rn: "resort",
    mo: "use_month_in",
    or: "origin_in",
    ls: "listing_status_in",
    withSold: "listing_status_nin",
    st: "stars_gte",
    so: "sort"
}
const filterName = (Object.keys(shortFilterName) as string[]).reduce(
    (res, key) => {
        res[shortFilterName[key]] = key
        return res
    },
    {} as Record<string, string>
)

function numberFormat(key: string, val: string) {
    try {
        return Number(val)
    } catch (e) {
        console.log(e)
        return filters[key]
    }
}

function arrayFormat(key: string, val: string) {
    let res = []
    try {
        res = Array.isArray(val) ? val : val.split(",")
    } catch (e) {
        console.error(e)
    }
    if (Array.isArray(res)) {
        return res
    }
    return filters[key]
}

/**
 * should return value parsed from string or default
 */
const formatters: Record<keyof typeof filters, (key: string, val: string) => any> = {
    price_lte: numberFormat,
    price_per_point_lte: numberFormat,
    points_on_contract_lte: numberFormat,
    points_on_contract_gte: numberFormat,
    resort: arrayFormat,
    use_month_in: arrayFormat,
    origin_in: arrayFormat,
    listing_status_in: arrayFormat,
    listing_status_nin: arrayFormat,
    stars_gte: numberFormat,
    sort: (s, v) => v || filters.sort
}

export function getQueryFromFilters(f: Partial<typeof filters>) {
    const data = {}
    filterKeys.forEach((key) => {
        if (key === "resort" && f[key]?.short_name_in?.length) {
            data[filterName[key]] = f[key].short_name_in
        } else if (key === "listing_status_nin" && !f[key]?.length) {
            data[filterName[key]] = 1
        } else {
            const val = f[key]
            // skip undefined and default values
            if (val !== undefined && !IsEqual(val, filters[key])) {
                data[filterName[key]] = val
            }
        }
    })

    return qs.stringify(data, {
        arrayFormat: "comma",
        encodeValuesOnly: true
    })
}

export const useFilterStore = create(
    combine(
        {
            ...filters,
            query: getQueryFromFilters(filters),
            lastListingsQuery: "",
            loading: false,
            open: false
        },
        (set, getState) => {
            let isResort = false
            const update = (f: Partial<ReturnType<typeof getState>>) => {
                const newState = {
                    ...getState(),
                    ...f
                }
                const query = getQueryFromFilters(isResort ? omit(newState, "resort") : newState)
                set({
                    ...f,
                    query
                })
            }
            return {
                updateLastQuery: () => {
                    set((s) => ({
                        lastListingsQuery: s.query
                    }))
                },
                init: async (resort?: { short_name: string; id: number }) => {
                    isResort = false
                    if (resort) {
                        isResort = true
                        update({
                            ...filters,
                            resort: {
                                short_name_in: [resort.short_name]
                            }
                        })
                        return
                    }
                    const search = qs.parse(window.location.search.replace("?", "") || getState().lastListingsQuery, {})
                    if (search.filter) {
                        update({
                            ...filters,
                            loading: true
                        })
                        try {
                            const s = await getSearchByCreatedAt(search.filter as string)
                            if (s?.search_query?.where) {
                                update({
                                    ...filters,
                                    ...s.search_query.where,
                                    sort: s.search_query.sort || "",
                                    loading: false
                                })
                                return
                            }
                            update({
                                ...filters,
                                loading: false
                            })
                        } catch (e) {
                            console.error(e)
                            update({
                                ...filters,
                                loading: false
                            })
                        }
                        return
                    }
                    const keys = Object.keys(search)

                    if (keys.length) {
                        const initFilters = {
                            ...filters
                        }
                        keys.forEach((key) => {
                            const keyName = shortFilterName[key]

                            if (keyName && formatters[keyName]) {
                                const val = search[key]
                                const formatedValue = formatters[keyName](keyName, val)
                                if (keyName === "resort") {
                                    initFilters[keyName] = {
                                        short_name_in: formatedValue
                                    }
                                } else if (key === "withSold") {
                                    initFilters.listing_status_nin = []
                                } else {
                                    initFilters[keyName] = formatedValue
                                }
                            }
                        })
                        update(initFilters)
                    }
                },
                setSavedSearch: (savedSearch: Partial<typeof filters> & { resort?: { short_name_in: string[] } }) => {
                    update({
                        ...filters,
                        ...savedSearch
                    })
                },
                clearAll: () => {
                    if (isResort) {
                        update({
                            ...omit(filters, "resort")
                        })
                    } else {
                        update({
                            ...filters
                        })
                    }
                },
                toggleDrawerOpen: () => {
                    set({
                        open: !getState().open
                    })
                },
                onListChangeResort: (val: string[]) => {
                    update({
                        resort: {
                            short_name_in: val
                        }
                    })
                },
                onTagListChange: (key: string) => {
                    return (value: string[]) => {
                        const keyName = key + "_in"

                        update({
                            [keyName]: value?.length ? value : undefined
                        })
                    }
                },
                onStarsChange(val: number) {
                    update({
                        stars_gte: val
                    })
                },
                onInactiveChange: (checked: boolean) => {
                    const listing_status_nin = checked ? [] : ownNotActiveStatuses
                    update({
                        listing_status_nin
                    })
                },
                onRangeChange: (key: string, maxValue: number) => {
                    return (__, _, [val1, val2]) => {
                        let res: Partial<typeof filters>
                        const value1 = Math.round(val1)
                        const value2 = val2 && Math.round(val2)
                        if (value2) {
                            res = {
                                [key + "_gte"]: value1,
                                [key + "_lte"]: value2 > maxValue ? undefined : value2
                            }
                        } else {
                            res = {
                                [key + "_lte"]: value1 > maxValue ? undefined : value1
                            }
                        }
                        update(res)
                    }
                },
                updateSort(sort: string) {
                    update({
                        sort
                    })
                }
            }
        }
    )
)

export function pickFilterDataFromStore() {
    const state = useFilterStore.getState()
    const data: Record<string, any> = {}
    filterKeys.forEach((key) => {
        if (typeof state[key] === "number" && state[key] === filters[key]) {
            return
        }
        data[key] = state[key]
    })
    return data
}
